import React, { useMemo, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Box, Typography, Button, Grid } from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as FacilityIcon } from '../../../../../images/icons/assign_user.svg';
import { TextInputFormField } from '../../../../Shared/TextInputFormField';
import SelectFormField from '../../../../Shared/SelectFormField';
import { US_STATES } from '../../../../../shared/constants';
import MaskedInput from '../../../../Shared/MaskedInput';
import {
  CreateLocationData,
  CreateParentFacilityData,
} from '../../../../../store/facility/facilityTypes';
import { selectFieldStyles, textFieldStyles } from '../../../../../mainTheme';

const facilityValidationSchema = Yup.object({
  name: Yup.string().required('Facility name is required'),
  address1: Yup.string().required('Address is required'),
  address2: Yup.string(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zip_code: Yup.string().required('Zip code is required'),
  phone: Yup.string()
    .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number is not valid')
    .required('Phone number is required'),
});

export type FacilityMode = 'facility' | 'location';

export const FacilityModeValues = {
  Facility: 'facility' as FacilityMode,
  Location: 'location' as FacilityMode,
};

const isLocationMode = (mode?: FacilityMode) =>
  mode === FacilityModeValues.Location;

export const FacilityDetailsForm = ({
  onSubmit,
  onCancel,
  mode = 'facility',
}: {
  onSubmit: (values: CreateParentFacilityData | CreateLocationData) => void;
  onCancel: () => void;
  mode?: FacilityMode;
}) => {
  const states_list = useMemo(() => {
    if (US_STATES.length < 0) return [];
    return US_STATES.map((item) => {
      return { label: item.name, value: item.id };
    });
  }, []);

  // --- Validation control logic ---
  const [validateOnChange, setValidateOnChange] = useState(false);

  return (
    <Formik
      initialValues={{
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
      }}
      validationSchema={facilityValidationSchema}
      onSubmit={onSubmit}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnChange}
    >
      {({ isSubmitting, isValid, dirty, submitCount }) => {
        // Enable validation on change/blur after first submit
        if (submitCount > 0 && !validateOnChange) {
          setValidateOnChange(true);
        }
        return (
          <Form>
            <Box display="flex" flexDirection="column" gap={2}>
              <ProgressBar currentStep={1} totalSteps={2} />
              <FacilityIcon />
              <Typography>
                {isLocationMode(mode)
                  ? 'Add new facility location details'
                  : 'Add new facility details'}
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                <Field
                  label={
                    isLocationMode(mode) ? 'Location name' : 'Facility name'
                  }
                  name="name"
                  type="text"
                  placeholder={
                    isLocationMode(mode) ? 'Location name' : 'Facility name'
                  }
                  component={TextInputFormField}
                  sx={textFieldStyles}
                />
                <Field
                  label="Address"
                  name="address1"
                  placeholder="Address"
                  component={TextInputFormField}
                  sx={textFieldStyles}
                />
                <Field
                  label="Address 2 (optional)"
                  name="address2"
                  placeholder="Address 2 (optional)"
                  component={TextInputFormField}
                  sx={textFieldStyles}
                />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="City"
                      name="city"
                      placeholder="City"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="State"
                      name="state"
                      placeholder="State"
                      component={SelectFormField}
                      options={states_list}
                      useNewStyles
                      sx={selectFieldStyles}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="Zip code"
                      name="zip_code"
                      placeholder="Zip code"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="Phone number"
                      name="phone"
                      format="(###) ###-####"
                      placeholder="Phone number"
                      component={MaskedInput}
                      sx={textFieldStyles}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}
              >
                <Button
                  variant="text"
                  color="secondary"
                  onClick={onCancel}
                  sx={{
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: '18px',
                    letterSpacing: '0.2px',
                    height: '44px',
                    width: '180px',
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                  sx={{
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: '18px',
                    letterSpacing: '0.2px',
                    height: '44px',
                    width: '180px',
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};
